import { ensureSlash } from '@/lib/ensure-slash'

export interface LoginRecoveryLocation {
	origin: string
	pathname: string
	search: string
	hash: string
}

export interface LoginRecoveryRedirectParams {
	basePath?: string
	isOidcEnabled: boolean
	location: LoginRecoveryLocation
	loginRequired: boolean
}

let pendingLoginRecoveryHref: string | undefined

function normalizeBasePath(basePath = '') {
	if (!basePath || basePath === '/') return ''

	const withLeadingSlash = basePath.startsWith('/') ? basePath : `/${basePath}`
	return withLeadingSlash.endsWith('/')
		? withLeadingSlash.slice(0, -1)
		: withLeadingSlash
}

function normalizeRoutePath(pathname = '/') {
	if (!pathname) return '/'
	if (pathname === '/') return '/'

	const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`
	return withLeadingSlash.endsWith('/')
		? withLeadingSlash.slice(0, -1)
		: withLeadingSlash
}

function stripBasePath(pathname: string, basePath = '') {
	const normalizedBasePath = normalizeBasePath(basePath)
	const normalizedPathname = normalizeRoutePath(pathname)

	if (!normalizedBasePath) return normalizedPathname
	if (normalizedPathname === normalizedBasePath) return '/'
	if (!normalizedPathname.startsWith(`${normalizedBasePath}/`)) {
		return normalizedPathname
	}

	const strippedPathname = normalizedPathname.slice(normalizedBasePath.length)
	return strippedPathname || '/'
}

export function shouldSuppressLoginRecovery(pathname: string, basePath = '') {
	const routePath = stripBasePath(pathname, basePath)
	return ['/login', '/auth', '/auth/callback', '/auth/silent'].includes(
		routePath,
	)
}

export function getLoginRecoveryReturnTarget(
	location: Omit<LoginRecoveryLocation, 'origin'>,
	basePath = '',
) {
	const routePath = stripBasePath(location.pathname, basePath)
	return `${routePath}${location.search}${location.hash}`
}

export function getLoginRecoveryRedirectHref({
	basePath = '',
	isOidcEnabled,
	location,
	loginRequired,
}: LoginRecoveryRedirectParams) {
	if (!loginRequired || !isOidcEnabled) return null
	if (shouldSuppressLoginRecovery(location.pathname, basePath)) return null

	const returnTarget = getLoginRecoveryReturnTarget(location, basePath)
	return `${location.origin}${ensureSlash(normalizeBasePath(basePath))}login?to=${encodeURIComponent(returnTarget)}`
}

export function redirectToLoginOnce(
	href: string,
	navigate: (nextHref: string) => void = (nextHref) => {
		globalThis.location.assign(nextHref)
	},
) {
	if (pendingLoginRecoveryHref === href) return false

	pendingLoginRecoveryHref = href
	navigate(href)
	return true
}

export function resetLoginRecoveryRedirectGuard() {
	pendingLoginRecoveryHref = undefined
}
